import { useState, useEffect, useCallback, useRef } from 'react';
import { isTerminalExtractStatus, type ExtractRunSession } from '@/services/api';
import { toUserFacingExtractionErrorMessage } from '@/lib/extractionErrorMessage';

export type WebSocketStatus = 'connecting' | 'open' | 'closed' | 'error';

// Updated interface to match the actual WebSocket message format
export interface WebSocketMessage {
  type?: string;
  screenshot_url?: string;
  json?: Record<string, unknown>;
  error?: string;
  message?: string;
  status?: string;
  step?: string;
  retryable?: boolean;
}

export interface WebSocketState {
  status: WebSocketStatus;
  messages: WebSocketMessage[];
  socket: WebSocket | null;
  screenshot_url: string | null;
  json: Record<string, unknown> | null;
  isLoading: boolean;
  statusMessage: string | null;
  error: string | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds
const CONNECTION_TIMEOUT = 10000; // 10 seconds

const rawWsBase = process.env.NEXT_PUBLIC_WS_URL || 'wss://www.s-shot.ru';
const WS_BASE_URL = rawWsBase.replace('http://', 'ws://').replace('https://', 'wss://').replace(/\/$/, '');

function buildInitialState(run: ExtractRunSession | null): WebSocketState {
  const terminal = isTerminalExtractStatus(run?.status);
  const error = run?.error ? toUserFacingExtractionErrorMessage(run.error) : null;

  return {
    status: error ? 'error' : terminal ? 'closed' : 'connecting',
    messages: [],
    socket: null,
    screenshot_url: run?.screenshotUrl ?? null,
    json: run?.json ?? null,
    isLoading: !terminal && !error && Boolean(run?.runId),
    statusMessage:
      !terminal && !error
        ? typeof run?.step === 'string' && run.step.length > 0
          ? `Выполняем шаг: ${run.step}…`
          : null
        : null,
    error,
  };
}

function resolveWebSocketUrl(run: ExtractRunSession | null): string | null {
  if (!run || !run.runId) {
    return null;
  }

  const candidate = run.websocketUrl ?? run.websocketPath;
  if (candidate) {
    if (candidate.startsWith('ws://') || candidate.startsWith('wss://')) {
      return candidate;
    }
    if (candidate.startsWith('/')) {
      return `${WS_BASE_URL}${candidate}`;
    }
    return `${WS_BASE_URL}/${candidate}`;
  }

  if (run.websocketToken) {
    return `${WS_BASE_URL}/ws/${run.runId}?token=${encodeURIComponent(run.websocketToken)}`;
  }

  return `${WS_BASE_URL}/ws/${run.runId}`;
}

export const useWebSocket = (run: ExtractRunSession | null) => {
  const [displayedStatusMessage, setDisplayedStatusMessage] = useState<string | null>(null);
  const [state, setState] = useState<WebSocketState>({
    status: 'closed',
    messages: [],
    socket: null,
    screenshot_url: null,
    json: null,
    isLoading: false,
    statusMessage: null,
    error: null,
  });

  const retryCount = useRef(0);
  const connectionTimeout = useRef<NodeJS.Timeout>();
  const previousRunId = useRef<string | null>(null);
  const hasReceivedJson = useRef(false);

  const resetStateForJob = useCallback(() => {
    hasReceivedJson.current = Boolean(run?.json || run?.error || isTerminalExtractStatus(run?.status));
    const nextState = buildInitialState(run);
    setState(nextState);
    setDisplayedStatusMessage(nextState.statusMessage);
  }, [run]);

  const prepareReconnectState = useCallback(() => {
    setState(prev => ({
      ...prev,
      status: 'connecting',
      socket: null,
      isLoading: true,
      statusMessage: prev.statusMessage,
      error: null,
    }));
  }, []);

  // Close any existing connection
  const closeExistingConnection = useCallback(() => {
    clearTimeout(connectionTimeout.current);
    
    setState(prev => {
      if (prev.socket && prev.socket.readyState !== WebSocket.CLOSED) {
        prev.socket.close(1000, 'New connection requested');
      }
      return prev;
    });
  }, []);

  const createWebSocketConnection = useCallback(() => {
    const jobId = run?.runId ?? null;
    if (!jobId) {
      return;
    }

    const isNewJob = previousRunId.current !== jobId;
    if (isNewJob) {
      closeExistingConnection();
      previousRunId.current = jobId;
      resetStateForJob();
    } else {
      prepareReconnectState();
    }

    let cancelled = false;
    let socket: WebSocket | null = null;

    const connect = async () => {
      try {
        const wsUrl = resolveWebSocketUrl(run);
        if (!wsUrl) {
          throw new Error('Не удалось получить параметры подключения');
        }

        socket = new WebSocket(wsUrl);

        connectionTimeout.current = setTimeout(() => {
          if (socket && socket.readyState !== WebSocket.OPEN) {
            console.error('WebSocket connection timeout');
            socket.close();
            setState(prev => ({
              ...prev,
              status: 'error',
              isLoading: false,
              error: 'Сервер слишком долго не отвечает',
            }));
          }
        }, CONNECTION_TIMEOUT);

        socket.onopen = () => {
          clearTimeout(connectionTimeout.current);
          retryCount.current = 0;

          setState(prev => ({
            ...prev,
            status: 'open',
            socket,
            statusMessage: null,
            error: null,
          }));
        };

        socket.onmessage = (event) => {
          try {
            let message: WebSocketMessage;

            try {
              message = JSON.parse(event.data);
            } catch (e) {
              message = {
                type: 'status',
                status: typeof event.data === 'string' ? event.data : 'Неизвестный формат сообщения',
              };
            }

            setState(prev => {
              const updatedState = { ...prev };
              updatedState.messages = [...prev.messages, message];

              if (message.type === 'progress' && message.step) {
                updatedState.statusMessage = `Выполняем шаг: ${message.step}…`;
              }

              if (message.screenshot_url) {
                updatedState.screenshot_url = message.screenshot_url;
                updatedState.statusMessage = null;
              }

              const payloadData = (message as any).data || message.json;
              if (payloadData) {
                hasReceivedJson.current = true;
                updatedState.json = payloadData as Record<string, unknown>;
                updatedState.isLoading = false;
                updatedState.statusMessage = null;

                setTimeout(() => {
                  if (socket && socket.readyState === WebSocket.OPEN) {
                    socket.close(1000, 'Data received');
                  }
                }, 100);
              }

              if (message.error || message.type === 'error') {
                const errorMessage =
                  typeof message.message === 'string'
                    ? message.message
                    : typeof message.error === 'string'
                      ? message.error
                      : 'Не удалось завершить извлечение';
                const userFacingError = toUserFacingExtractionErrorMessage(errorMessage);
                hasReceivedJson.current = true;
                updatedState.error = userFacingError;
                updatedState.isLoading = false;
                updatedState.statusMessage = null;
              }

              if (message.type === 'done' || message.status === 'completed' || message.status === 'success') {
                hasReceivedJson.current = true;
                updatedState.isLoading = false;
                updatedState.statusMessage = null;
              }

              return updatedState;
            });
          } catch (error) {
            console.error('Error handling WebSocket message:', error, event.data);
            setState(prev => ({
              ...prev,
              error: 'Не удалось обработать сообщение сервера',
            }));
          }
        };

        socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          clearTimeout(connectionTimeout.current);
          setState(prev => ({
            ...prev,
            status: 'error',
            isLoading: false,
            statusMessage: null,
            error: 'Соединение прервалось',
          }));
        };

        socket.onclose = (event) => {
          clearTimeout(connectionTimeout.current);

          const missingJson = !hasReceivedJson.current;
          const shouldRetry =
            (event.code !== 1000 || missingJson) && retryCount.current < MAX_RETRIES;

          if (shouldRetry) {
            retryCount.current++;
            setState(prev => ({
              ...prev,
              status: 'connecting',
              socket: null,
              isLoading: true,
              statusMessage: missingJson ? (prev.statusMessage ?? 'Ожидаем результаты извлечения…') : prev.statusMessage,
              error: prev.error,
            }));
            if (missingJson) {
              setDisplayedStatusMessage(prev => prev ?? 'Ожидаем результаты извлечения…');
            }
            setTimeout(createWebSocketConnection, RETRY_DELAY);
          } else {
            setState(prev => ({
              ...prev,
              status: 'closed',
              socket: null,
              isLoading: false,
              statusMessage: missingJson ? (prev.statusMessage ?? 'Ожидаем результаты извлечения…') : null,
              error:
                prev.error ??
                (event.code !== 1000 && !missingJson ? 'Соединение неожиданно закрылось' : null),
            }));
            if (missingJson) {
              setDisplayedStatusMessage(prev => prev ?? 'Ожидаем результаты извлечения…');
            }
          }
        };
      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        clearTimeout(connectionTimeout.current);
        setState(prev => ({
          ...prev,
          status: 'error',
          isLoading: false,
          statusMessage: null,
          error: error instanceof Error ? error.message : 'Не удалось установить соединение',
        }));
      }
    };

    connect().catch((error) => {
      console.error('Unhandled WebSocket connection error:', error);
    });

    return () => {
      cancelled = true;
      clearTimeout(connectionTimeout.current);
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        socket.close(1000, 'Component unmounted');
      }
    };
  }, [
    run,
    resetStateForJob,
    prepareReconnectState,
    closeExistingConnection,
  ]);

  useEffect(() => {
    // Only create a connection if we have a valid job ID
    if (run?.runId && run.runId !== 'pending') {
      if (isTerminalExtractStatus(run.status)) {
        resetStateForJob();
        return;
      }
      const cleanup = createWebSocketConnection();
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, [createWebSocketConnection, resetStateForJob, run]);

  const closeConnection = useCallback(() => {
    clearTimeout(connectionTimeout.current);
    if (state.socket && state.socket.readyState === WebSocket.OPEN) {
      state.socket.close(1000, 'Manual close');
    }
    setState(prev => ({ 
      ...prev, 
      status: 'closed', 
      socket: null,
      error: null
    }));
  }, [state.socket]);

  const combinedStatusMessage = state.statusMessage ?? (state.status === 'closed' ? null : displayedStatusMessage);
  return { ...state, statusMessage: combinedStatusMessage, closeConnection };
};
